import React, { useState } from 'react'
import { Icon, Image, Label, Table } from 'semantic-ui-react'
import at_risk from '../../../../../assets/svg/at_risk_label.svg'
import off_track from '../../../../../assets/svg/off_track_label.svg'
import on_track from '../../../../../assets/svg/on_track_label.svg'
import { useWindowSize } from '../../../../../hooks/useWindowSize'
import { abbreviateString } from '../../../../../utils/abbreviateString'
import { currencyFormat } from '../../../../../utils/currencyFormat'
import { taskDisplayName } from '../../../../../utils/formatters'
import { IDirectCost } from '../../../types/directAndPassTroughCosts'
import { projectStatus } from '../../../types/project'
import '../BudgetTableComponent.css'


interface StateProps {
  costs: IDirectCost[]
  name: string
  color: string | undefined
  renderHealthLabel: JSX.Element | null
  calcExpenditures: number
  calcForecast: number
  calcProfit: number
  calcBudget: number
  calcTotalBillableAmount: number
  editRight: boolean
  statusCompleted: boolean
  selectedCost: IDirectCost
  idx: number
  billingProcedure?: string
  setOpenDetailsModal: (open: boolean) => void
  setSelectedCost: (cost: IDirectCost) => void
}

const ServiceProviderBudgetTable = (props: StateProps): JSX.Element => {
  const {
    billingProcedure,
    calcBudget,
    calcExpenditures,
    calcForecast,
    calcProfit,
    calcTotalBillableAmount,
    color,
    costs,
    editRight,
    name,
    renderHealthLabel,
    statusCompleted,
    selectedCost,
    idx,
    setOpenDetailsModal,
    setSelectedCost
  } = props
  const [collapseBudget, setCollapseBudget] = useState(true)

  const size = useWindowSize().width

  const calcHealth = (dc: IDirectCost) => {
    const budget = dc.budget ?? 0
    const forecast = dc.forecast ?? 0
    let health = ''
    if (dc.status !== 'completed') {
      if (forecast <= budget) {
        health = 'on_track'
      }
      if (forecast > budget) {
        health = 'at_risk'
      }
      if (dc.expenditures > budget) {
        health = 'off_track'
      }
    } else {
      if (dc.expenditures <= budget) {
        health = 'on_track'
      }
      if (dc.expenditures > budget) {
        health = 'off_track'
      }
    }
    return health
  }

  const renderCollapseRows = () => {
    return (
      <Table.Row style={{ boxShadow: 'inset 0rem 0rem 10rem #F4F7F9', borderRadius: 0, height: '50px' }}>
        <Table.Cell colSpan={5} style={{ borderTop: idx === 0 ? 'none' : '4px solid white' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', paddingLeft: '40px' }}>
            <Icon
              name={collapseBudget ? 'chevron circle down' : 'chevron circle right'}
              style={{ margin: 0, color: color ? color : '#172533', width: '14px', height: '14px' }}
              onClick={() => setCollapseBudget(!collapseBudget)}
            />
            <div style={{ fontSize: '1.28rem', color: color ? color : '#172533', marginLeft: '8px', letterSpacing: '.02rem', fontWeight: 'bold' }}>{name}</div>
          </div>
        </Table.Cell>
        <Table.Cell style={{ borderTop: idx === 0 ? 'none' : '4px solid white', fontWeight: 500 }}>
          <div style={{ display: 'flex', float: 'right', color: '#232C58', fontSize: '1rem' }}>
            <p style={{ margin: 0 }}>{currencyFormat(calcBudget)[0]} {currencyFormat(calcBudget).split(',')[0].replace(currencyFormat(calcBudget)[0], '')}</p>
            <p style={{ color: '#BEC8C3' }}>,{currencyFormat(calcBudget).split(',')[1]}</p>
          </div>
        </Table.Cell>
        {billingProcedure == 'billing_on_actual' && <Table.Cell style={{ borderTop: idx === 0 ? 'none' : '4px solid white', fontWeight: 500 }}>
          <div style={{ display: 'flex', float: 'right', color: '#232C58', fontSize: '1rem' }}>
            <p style={{ margin: 0 }}>{currencyFormat(calcTotalBillableAmount)[0]} {currencyFormat(calcTotalBillableAmount).split(',')[0].replace(currencyFormat(calcTotalBillableAmount)[0], '')}</p>
            <p style={{ color: '#BEC8C3' }}>,{currencyFormat(calcTotalBillableAmount).split(',')[1]}</p>
          </div>
        </Table.Cell>}
        <Table.Cell style={{ borderTop: idx === 0 ? 'none' : '4px solid white', fontWeight: 500 }}>
          <div style={{ display: 'flex', float: 'right', color: '#232C58', fontSize: '1rem' }}>
            <p style={{ margin: 0 }}>{currencyFormat(calcExpenditures)[0]} {currencyFormat(calcExpenditures).split(',')[0].replace(currencyFormat(calcExpenditures)[0], '')}</p>
            <p style={{ color: '#BEC8C3' }}>,{currencyFormat(calcExpenditures).split(',')[1]}</p>
          </div>
        </Table.Cell>
        <Table.Cell style={{ borderTop: idx === 0 ? 'none' : '4px solid white', fontWeight: 500 }} className={size > 1400 ? '' : 'last-visable-table-cell'}>
          <div style={{ paddingRight: '6px', display: 'flex', float: 'right', color: '#232C58', fontSize: '1rem' }}>
            <p style={{ margin: 0 }}>{currencyFormat(calcForecast)[0]} {currencyFormat(calcForecast).split(',')[0].replace(currencyFormat(calcForecast)[0], '')}</p>
            <p style={{ color: '#BEC8C3' }}>,{currencyFormat(calcForecast).split(',')[1]}</p>
          </div>
        </Table.Cell>
        <Table.Cell style={{ borderTop: idx === 0 ? 'none' : '4px solid white', fontWeight: 500 }}>
          {calcProfit !== 0 ?
            <p style={{ color: statusCompleted ? '#232C58' : '#747678', textAlign: 'center', fontSize: '1rem' }}>{calcProfit.toFixed(2)}%
            </p> : <p style={{ color: '#232C58', textAlign: 'center', fontSize: '1rem' }}>- %</p>}
        </Table.Cell>
        {(billingProcedure !== 'billing_on_actual' || size > 1455) && <Table.Cell textAlign='left' style={{ borderTop: idx === 0 ? 'none' : '4px solid white' }}>{renderHealthLabel}</Table.Cell>}
      </Table.Row>
    )
  }

  const handleOpenModal = (cost: IDirectCost) => {
    setOpenDetailsModal(true)
    setSelectedCost(cost)
  }

  const renderRows = () => {
    return costs.map((a, idx) => {
      const external = a.external_expenditures ?? 0
      const expenditures = a.expenditures + external
      const forecast = a.forecast ?? 0
      return (
        <Table.Row key={idx} className={collapseBudget ? 'show-row' : 'collapse-row'} style={{ background: idx % 2 ? '#F9FBFC' : '' }}>
          <Table.Cell className={idx % 2 ? 'custom-left-border-cell' : ''} colSpan={3} id='disableCloseModal' style={{ cursor: 'pointer', border: 'none' }} onClick={() => editRight ? handleOpenModal(a) : undefined}>
            <p
              id='disableCloseModal'
              style={{
                cursor: editRight ? 'pointer' : 'default',
                paddingLeft: '40px',
                color: selectedCost.id === a.id ? '#08D6A1' : '#232C58',
                textAlign: 'left',
                textDecorationLine: selectedCost.id === a.id ? 'underline' : 'none',
                fontSize: '14px',
                fontWeight: 600
              }}>{taskDisplayName(a.key, a.offer_position)}
            </p>
          </Table.Cell>

          <Table.Cell style={{ borderTop: 'none' }} textAlign='left'>
            <Label
              content={abbreviateString(a.project_phase.name, size > 1620 ? 15 : 11, true, size > 1620 ? 17 : 14)}
              style={{ background: a.project_phase?.color + '33', color: a.project_phase?.color, borderRadius: '8px', height: '26px' }}
            />
          </Table.Cell>

          <Table.Cell style={{ borderTop: 'none' }}>
            <p style={{ color: '#232C58', textAlign: 'left' }}>
              {a.department.name}
            </p>
          </Table.Cell>

          <Table.Cell style={{ borderTop: 'none', fontWeight: 500 }}>
            {a.budget !== undefined && a.budget !== 0 ?
              <div style={{ display: 'flex', float: 'right', color: '#232C58' }}>
                <p style={{ margin: 0}}>{currencyFormat(a.budget)[0]} {currencyFormat(a.budget).split(',')[0].replace(currencyFormat(a.budget)[0], '')}</p>
                <p style={{ color: '#BEC8C3'}}>,{currencyFormat(a.budget).split(',')[1]}</p>
              </div>
              : <p style={{ color: '#232C58', textAlign: 'right' }}>- €</p>}
          </Table.Cell>

          {billingProcedure === 'billing_on_actual' ? <Table.Cell style={{ borderTop: 'none', fontWeight: 500 }}>
            {a.billable_amount !== undefined ?
              <div style={{ display: 'flex', float: 'right', color: '#232C58' }}>
                <p style={{ margin: 0 }}>{currencyFormat(a.billable_amount)[0]} {currencyFormat(a.billable_amount).split(',')[0].replace(currencyFormat(a.billable_amount)[0], '')}</p>
                <p style={{ color: '#BEC8C3' }}>,{currencyFormat(a.billable_amount).split(',')[1]}</p>
              </div>
              : <p style={{ color: '#232C58', textAlign: 'right' }}>- €</p>}
          </Table.Cell> : null}

          <Table.Cell style={{ borderTop: 'none', fontWeight: 500 }}>
            {a.expenditures !== undefined ?
              <div style={{ display: 'flex', float: 'right', color: '#232C58' }}>
                <p style={{ margin: 0}}>{currencyFormat(expenditures)[0]} {currencyFormat(expenditures).split(',')[0].replace(currencyFormat(expenditures)[0], '')}</p>
                <p style={{ color: '#BEC8C3' }}>,{currencyFormat(expenditures).split(',')[1]}</p>
              </div>
              : <p style={{ color: '#232C58', textAlign: 'right' }}>- €</p>}
          </Table.Cell>

          <Table.Cell style={{ borderTop: 'none', fontWeight: 500 }} className={size > 1400 ? '' : 'last-visable-table-cell'}>
            {a.forecast !== undefined ?
              <div style={{ display: 'flex', float: 'right', color: '#232C58', paddingRight: '6px' }}>
                <p style={{ margin: 0 }}>{currencyFormat(forecast)[0]} {currencyFormat(forecast).split(',')[0].replace(currencyFormat(forecast)[0], '')}</p>
                <p style={{ color: '#BEC8C3' }}>,{currencyFormat(forecast).split(',')[1]}</p>
              </div>
              : <p style={{ color: '#232C58', textAlign: 'right', paddingRight: '6px' }}>- €</p>}
          </Table.Cell>

          <Table.Cell style={{ borderTop: 'none', fontWeight: 500 }} textAlign='center'>
            {a.profit !== undefined && a.profit !== 0 ?
              <p style={{ color: a.status === 'completed' ? '#232C58' : '#747678', textAlign: 'center' }}>{a.profit}%</p>
              : <p style={{ color: '#232C58', textAlign: 'center' }}>- %</p>}
          </Table.Cell>

          {(billingProcedure !== 'billing_on_actual' ||  size > 1455) && <Table.Cell style={{ borderTop: 'none' }} textAlign='left'>
            <Label
              image
              style={{
                backgroundColor: a.statusColor(calcHealth(a)).background,
                color: a.statusColor(calcHealth(a)).color,
                borderRadius: '8px',
                height: '26px'
              }}>
              <Image
                src={
                  calcHealth(a) === 'on_track' ? on_track
                    : calcHealth(a) === 'at_risk' ? at_risk
                      : calcHealth(a) === 'off_track' ? off_track
                        : undefined
                }
                style={{ maxWidth: '12px', marginLeft: '2px' }}
              />
              {projectStatus.find(s => s.key === calcHealth(a))?.name}
            </Label>
          </Table.Cell>}
        </Table.Row>
      )
    })
  }

  return (
    <React.Fragment>
      {renderCollapseRows()}
      {renderRows()}
    </React.Fragment>
  )
}

export default ServiceProviderBudgetTable
